// ============================================
// COMPONENT - Modal cerrar turno de caja (con arqueo opcional/obligatorio)
// ============================================

import { useState } from 'react';
import { Button } from '@almacenes/ui';
import { useConfiguracionCaja, useSession, type TurnoCaja } from '@almacenes/core';
import { MdClose } from 'react-icons/md';
import { DesglosePorMedioPago } from './DesglosePorMedioPago';
import { formatMontoInput } from '../utils/formato';

interface CerrarTurnoModalProps {
  turno: TurnoCaja;
  onClose: () => void;
  /** Botón "Cancelar" del formulario: por defecto es igual a onClose, pero un contenedor
   * con una vista previa (ej. el resumen de GestionCajaModal) puede pasar aquí un "volver"
   * distinto, dejando la X y el click en el fondo como el cierre completo (onClose). */
  onCancelar?: () => void;
  onConfirmar: (efectivoContado: number | undefined) => void;
  isLoading: boolean;
}

const formatCurrency = (value: number) => `$${Math.round(value).toLocaleString('es-CL')}`;

export const CerrarTurnoModal = ({ turno, onClose, onCancelar, onConfirmar, isLoading }: CerrarTurnoModalProps) => {
  const { data: configuracion, isLoading: isLoadingConfig } = useConfiguracionCaja();
  const { data: session } = useSession();
  const [montoDisplay, setMontoDisplay] = useState('');
  const [montoRaw, setMontoRaw] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  const arqueoObligatorio = configuracion?.arqueoObligatorio ?? false;

  const handleMontoChange = (value: string) => {
    const soloNumeros = value.replace(/\D/g, '');
    setMontoRaw(soloNumeros);
    setMontoDisplay(formatMontoInput(soloNumeros));
    if (error) setError(undefined);
  };

  const handleConfirmar = () => {
    if (arqueoObligatorio && !montoRaw) {
      setError('El conteo de efectivo es obligatorio para este almacén');
      return;
    }
    onConfirmar(montoRaw ? Number(montoRaw) : undefined);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-2xl font-bold" style={{ color: '#11194F' }}>
              Cerrar mi caja
            </h3>
            {/* Última confirmación antes de un cierre real: si el negocio tiene
                varias sucursales bajo el mismo RUT (nombre repetido), esto es
                lo único que permite verificar que es la sucursal correcta. */}
            {session?.almacen?.direccion && (
              <p className="text-xs text-gray-400 mt-0.5">📍 {session.almacen.direccion}</p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <MdClose className="text-2xl" />
          </button>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Total ventas del turno:</span>
            <span className="font-semibold" style={{ color: '#11194F' }}>{formatCurrency(turno.totalVentas ?? 0)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Ingresos del turno (todos los medios):</span>
            <span className="font-semibold" style={{ color: '#11194F' }}>{formatCurrency(turno.totalIngresos ?? 0)}</span>
          </div>
          {turno.totalIngresosEfectivo != null && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Ingresos en efectivo:</span>
              <span className="font-semibold" style={{ color: '#11194F' }}>{formatCurrency(turno.totalIngresosEfectivo)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Efectivo esperado en caja:</span>
            <span className="font-bold text-lg" style={{ color: '#22C55E' }}>{formatCurrency(turno.efectivoEsperado ?? 0)}</span>
          </div>
        </div>

        <div className="mb-4">
          <DesglosePorMedioPago turno={turno} />
        </div>

        {isLoadingConfig ? (
          <p className="text-sm text-gray-500 mb-4">Cargando configuración de arqueo...</p>
        ) : (
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2" style={{ color: '#11194F' }}>
              Efectivo contado {arqueoObligatorio ? <span className="text-red-500">*</span> : <span className="text-gray-400">(opcional)</span>}
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
              <input
                type="text"
                inputMode="numeric"
                value={montoDisplay}
                onChange={(e) => handleMontoChange(e.target.value)}
                placeholder={String(turno.efectivoEsperado ?? 0)}
                className={`w-full pl-8 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                  error ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'
                }`}
                disabled={isLoading}
              />
            </div>
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
            {!arqueoObligatorio && (
              <p className="text-gray-500 text-xs mt-1">
                Puedes contar el efectivo del cajón igual, aunque no sea obligatorio en este almacén.
              </p>
            )}
          </div>
        )}

        <div className="flex gap-3">
          <Button variant="secondary" onClick={onCancelar ?? onClose} disabled={isLoading} className="flex-1">
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleConfirmar} isLoading={isLoading} disabled={isLoading || isLoadingConfig} className="flex-1">
            Cerrar caja
          </Button>
        </div>
      </div>
    </div>
  );
};
